import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import TargetGroupModals from '../../TargetGroupView/TargetGroupModals/TargetGroupModals';
import {
    ISearchResponseContext,
    SearchedParticipant,
} from '../../../store/api/search';
import SearchTable from './SearchTable';
import TargetGroupTableFooter from '../../TargetGroupView/TargetGroupTable/TargetGroupTableFooter/TargetGroupTableFooter';
import {
    useBatchProcessParticipantMutation,
    useSendParticipantEmailsMutation,
    useSendResetPasswordEmailsMutation,
} from '../../../store/api/participants';
import { useLazyGetGroupQuery } from '../../../store/api/groups';
import { useLazyGetCompanyQuery } from '../../../store/api/companies';
import styles from '../../TargetGroupView/TargetGroupView.module.css';

interface SearchTableProps {
    results: ISearchResponseContext[];
    isSuperAdmin: boolean;
    isSearchLoading: boolean;
    participantHasMultipleCompanies: boolean;
    currentPage: number;
    refetchSearch: () => void;
    setPageToFirst: () => void;
}

const SearchTableView: React.FC<SearchTableProps> = ({
    results,
    currentPage,
    isSuperAdmin,
    refetchSearch,
    setPageToFirst,
    participantHasMultipleCompanies,
    isSearchLoading,
}) => {
    const [triggerGetCompany] = useLazyGetCompanyQuery();
    const [triggerGetGroup] = useLazyGetGroupQuery();
    const [batchProcessParticipant, { isLoading: isBatchProcessLoading }] =
        useBatchProcessParticipantMutation();
    const [sendParticipantEmails] = useSendParticipantEmailsMutation();
    const [sendResetPasswordEmails] = useSendResetPasswordEmailsMutation();

    const [modals, setModals] = useState({
        remove: false,
        fullDelete: false,
        resetScan: false,
        reminder: false,
        resetPassword: false,
        report: false,
    });
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isParticipantDataLoading, setIsParticipantDataLoading] =
        useState(false);
    const [participantData, setParticipantData] = useState<
        Record<
            string,
            {
                companyName: string;
                groupName: string;
                groupType?: string;
                hasOptOutQuestionnaire: boolean;
            }
        >
    >({});
    const [usersShowingInfo, setUsersShowingInfo] = useState<string[]>([]);

    const [searchResults, setSearchResults] = useState(results);
    const [isPageLoaded, setIsPageLoaded] = useState(false);

    useEffect(() => {
        if (!isSearchLoading) {
            setIsPageLoaded(true);
        }
    }, [isSearchLoading]);

    useEffect(() => {
        setSearchResults(results);
    }, [results]);

    const uniqueKeys = useMemo(() => {
        const keys = new Set<string>();
        results.forEach((r) => keys.add(`${r.companyId}||${r.groupId}`));
        return Array.from(keys);
    }, [results]);

    const activeTriggersRef = useRef<Array<{ abort: () => void }>>([]);

    const fetchCompanyGroupNames = useCallback(async () => {
        activeTriggersRef.current.forEach((t) => t.abort());
        activeTriggersRef.current = [];

        setIsParticipantDataLoading(true);

        try {
            const entries = await Promise.all(
                uniqueKeys.map(async (key) => {
                    const [companyId, groupId] = key.split('||');

                    const companyTrigger = triggerGetCompany(companyId);
                    const groupTrigger = triggerGetGroup({
                        companyIdentifier: companyId,
                        groupId,
                    });

                    activeTriggersRef.current.push(
                        companyTrigger,
                        groupTrigger,
                    );

                    try {
                        const [company, group] = await Promise.all([
                            companyTrigger.unwrap(),
                            groupTrigger.unwrap(),
                        ]);

                        return [
                            key,
                            {
                                companyName: company.name,
                                groupName: group.name,
                                groupType: group.type as string | undefined,
                                hasOptOutQuestionnaire:
                                    !!group.config?.has_opt_out_questionnaire,
                            },
                        ];
                    } catch (err) {
                        if (err instanceof Error && err.name === 'AbortError')
                            return null;
                        console.error('Error fetching group/company info', err);
                        return [
                            key,
                            {
                                companyName: companyId,
                                groupName: groupId,
                                groupType: undefined as string | undefined,
                                hasOptOutQuestionnaire: false,
                            },
                        ];
                    }
                }),
            );

            const validEntries = entries.filter(
                (
                    entry,
                ): entry is [
                    string,
                    {
                        companyName: string;
                        groupName: string;
                        groupType: string | undefined;
                        hasOptOutQuestionnaire: boolean;
                    },
                ] => entry !== null,
            );
            setParticipantData(Object.fromEntries(validEntries));
        } finally {
            setIsParticipantDataLoading(false);
        }
    }, [uniqueKeys, triggerGetCompany, triggerGetGroup]);

    useEffect(() => {
        if (results.length) {
            void fetchCompanyGroupNames();
        }

        return () => {
            activeTriggersRef.current.forEach((t) => t.abort());
        };
    }, [results, fetchCompanyGroupNames]);

    const participants: SearchedParticipant[] = useMemo(() => {
        return searchResults.map((searchResult) => {
            const key = `${searchResult.companyId}||${searchResult.groupId}`;
            const companyGroupNames = participantData[key] || {
                companyName: '',
                groupName: '',
                groupType: undefined as string | undefined,
                hasOptOutQuestionnaire: false,
            };

            return {
                company_id: searchResult.companyId,
                company_slug: searchResult.companySlug,
                company_name: companyGroupNames.companyName,
                group_id: searchResult.groupId,
                scan_template_id: searchResult.scanTemplateId,
                group_name: companyGroupNames.groupName,
                group_type: companyGroupNames.groupType,
                has_opt_out_questionnaire:
                    companyGroupNames.hasOptOutQuestionnaire,
                user_id: searchResult.userId,
                invitation_id: searchResult.invitationId,
                first_name: searchResult.firstName,
                last_name: searchResult.lastName,
                email: searchResult.email,
                invited_at: searchResult.invitedAt,
                user_registered_at: searchResult.invitedAt,
                report_generated_at: searchResult.reportGeneratedAt,
                report_id: searchResult.reportId,
                is_active: searchResult.isActive,
            };
        });
    }, [searchResults, participantData]);

    const onUserShowInfoChange = (userId: string) => {
        setUsersShowingInfo((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId],
        );
    };

    const groupSelectedByCompanyAndGroup = useCallback(() => {
        const grouped: Record<string, SearchedParticipant[]> = {};

        participants.forEach((p) => {
            const participantKey = `${p.user_id}||${p.group_id}`;
            if (selectedIds.has(participantKey)) {
                const groupKey = `${p.company_id}||${p.group_id}`;
                if (!grouped[groupKey]) grouped[groupKey] = [];
                grouped[groupKey].push(p);
            }
        });

        return grouped;
    }, [participants, selectedIds]);

    const onUserSelectChange = useCallback(
        (userId: string, groupId: string, event: any) => {
            const key = `${userId}||${groupId}`;
            setSelectedIds((prev) => {
                const newSet = new Set(prev);

                if (event.target.checked) {
                    newSet.add(key);
                } else {
                    newSet.delete(key);
                }

                return newSet;
            });
        },
        [],
    );

    const onUserSelectChangeAll = useCallback(() => {
        if (selectedIds.size === participants.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(
                new Set(participants.map((p) => `${p.user_id}||${p.group_id}`)),
            );
        }
    }, [participants, selectedIds.size]);

    const filterRemovedParticipants = useCallback(
        (prev: ISearchResponseContext[], deletedKeys: Set<string>) => {
            return prev.filter(
                (p) => !deletedKeys.has(`${p.userId}||${p.groupId}`),
            );
        },
        [],
    );

    const discardModals = () =>
        setModals({
            remove: false,
            fullDelete: false,
            resetScan: false,
            reminder: false,
            resetPassword: false,
            report: false,
        });

    const sendParticipantsResetPasswordEmails = () => {
        const grouped = groupSelectedByCompanyAndGroup();
        Object.entries(grouped).forEach(([key, groupParticipants]) => {
            const [, groupId] = key.split('||');
            sendResetPasswordEmails({
                groupId,
                userIds: groupParticipants.map((p) => p.user_id),
            });
        });
        discardModals();
    };

    const sendReminderEmails = () => {
        const grouped = groupSelectedByCompanyAndGroup();
        Object.entries(grouped).forEach(([key, groupParticipants]) => {
            const [companyId, groupId] = key.split('||');
            sendParticipantEmails({
                companyIdentifier: companyId,
                groupId,
                emailType: 'reminder',
                userIds: groupParticipants.map((p) => p.user_id),
                resend: true,
            });
        });
        discardModals();
    };

    const sendReportEmails = () => {
        const grouped = groupSelectedByCompanyAndGroup();
        Object.entries(grouped).forEach(([key, groupParticipants]) => {
            const [companyId, groupId] = key.split('||');
            const participantsWithReport = groupParticipants.filter(
                (p) => !!p.report_generated_at,
            );
            if (participantsWithReport.length > 0) {
                sendParticipantEmails({
                    companyIdentifier: companyId,
                    groupId,
                    emailType: 'report',
                    userIds: participantsWithReport.map((p) => p.user_id),
                    resend: true,
                });
            }
        });
        discardModals();
    };

    const resetCandidateScan = async () => {
        const grouped = groupSelectedByCompanyAndGroup();
        for (const [key, groupParticipants] of Object.entries(grouped)) {
            const [companyId, groupId] = key.split('||');
            await batchProcessParticipant({
                companyIdentifier: companyId,
                groupId,
                operation: 'reset',
                userIds: groupParticipants.map((p) => p.user_id),
            });
        }
        discardModals();
        setSelectedIds(new Set());
        await new Promise((res) => setTimeout(res, 1000));
        refetchSearch();
    };

    const handleDelete = async (operation: 'delete' | 'full_delete') => {
        const grouped = groupSelectedByCompanyAndGroup();
        const deletedKeys = new Set<string>();

        for (const [key, groupParticipants] of Object.entries(grouped)) {
            const [companyId, groupId] = key.split('||');

            const deletableParticipants = groupParticipants.filter(
                (p) => operation === 'full_delete' || !p.report_generated_at,
            );

            const userIds = deletableParticipants.map((p) => p.user_id);

            if (userIds.length > 0) {
                await batchProcessParticipant({
                    companyIdentifier: companyId,
                    groupId,
                    operation,
                    userIds,
                });

                deletableParticipants.forEach((p) => {
                    deletedKeys.add(`${p.user_id}||${p.group_id}`);
                });
            }
        }

        setSelectedIds(new Set());
        discardModals();

        setSearchResults((prev) =>
            filterRemovedParticipants(prev, deletedKeys),
        );

        if (currentPage > 0) {
            setPageToFirst();
            refetchSearch();
        }
    };

    return (
        <div className={styles.group_table_container}>
            <div className={styles.group_table_container__content}>
                <SearchTable
                    participants={participants}
                    selectedIds={selectedIds}
                    isSearchLoading={isSearchLoading}
                    isPageLoaded={isPageLoaded}
                    isBatchProcessLoading={isBatchProcessLoading}
                    onSelect={onUserSelectChange}
                    onSelectAll={onUserSelectChangeAll}
                    isParticipantDataLoading={isParticipantDataLoading}
                    isSuperAdmin={isSuperAdmin}
                    participantHasMultipleCompanies={
                        participantHasMultipleCompanies
                    }
                    usersShowingInfo={usersShowingInfo}
                    onUserShowInfoChange={onUserShowInfoChange}
                />
            </div>
            <TargetGroupTableFooter
                candidateCurrentPage={currentPage}
                candidateTotalPages={1}
                isBatchProcessLoading={isBatchProcessLoading}
                isSuperAdmin={isSuperAdmin}
                selectedIds={selectedIds}
                participants={participants}
                setSelectedParticipants={() => {}}
                onSendResetPasswordEvent={() =>
                    setModals({ ...modals, resetPassword: true })
                }
                onSendReminderEvent={() =>
                    setModals({ ...modals, reminder: true })
                }
                onSendReportEvent={() => setModals({ ...modals, report: true })}
                onRemoveCandidateEvent={() =>
                    setModals({ ...modals, remove: true })
                }
                onFullDeleteCandidateEvent={() =>
                    setModals({ ...modals, fullDelete: true })
                }
                onResetCandidateScanEvent={() =>
                    setModals({ ...modals, resetScan: true })
                }
                setSearchParams={() => {}}
            />
            <TargetGroupModals
                selectedIds={selectedIds}
                participants={participants}
                sendReminderEmails={sendReminderEmails}
                sendResetPasswordEmails={sendParticipantsResetPasswordEmails}
                sendReportEmails={sendReportEmails}
                removeCandidates={() => handleDelete('delete')}
                fullDeleteCandidates={() => handleDelete('full_delete')}
                resetCandidateScan={resetCandidateScan}
                deleteTargetGroup={() => {}}
                shouldShowInviteModal={false}
                shouldShowResetPasswordModal={modals.resetPassword}
                shouldShowReminderModal={modals.reminder}
                shouldShowReportModal={modals.report}
                shouldShowResetCandidateScanSuccessModal={false}
                shouldShowRemoveModal={modals.remove}
                shouldShowFullDeleteCandidateModal={modals.fullDelete}
                shouldShowResetCandidateScanModal={modals.resetScan}
                shouldShowDeleteTargetGroupModal={false}
                onDiscardModal={discardModals}
            />
        </div>
    );
};

export default SearchTableView;
